import { formatDate, formatPercent } from "@angular/common"
import { PaymentInfo } from "../interface/paymentInfo.interface"
import { Product } from "../interface/products/product.interface"
import { ProductTransaction } from "../interface/productTransaction.interface"
import DateUtils from "./dates.util"
import { Transaction } from "../interface/transaction.interface"
import { ProductCalculateData } from "../interface/productCalculate.interface"
import { CountInactiveTransactions } from "../interface/statistics/countInactiveTransactions.interface"
import { CountProductsData } from "../interface/statistics/countProducts.interface"
import { PaymentSubscribe } from "../interface/paymentSubscribe.interface"
import { Dependent } from "../interface/dependent.interface"
import { ProductTransactionCalculate } from "../interface/productTransactionCalculate.interface"
import { first, switchMap } from "rxjs"
import { input } from "@angular/core"

export default class ProductUtils{
  public static PROD_ISOIMG_ID = 1
  public static PROD_SSM_ID = 2
  public static PROD_TM_ID = 3
  public static PROD_LN_ID = 4
  public static PROD_ISOIMGGOLD_ID = 5

  public static PROD_STATUS_ACTIVE = 0

  /** Transactions Mode */
  public static TRANSACTION_MODE_NEW = "newBenefits"
  public static TRANSACTION_MODE_EDIT = "editBenefits"
  public static TRANSACTION_MODE_VIEW = "viewBenefits"

  public static VISATYPE = "VISATYPE"     // Documento de producto de tipo VISA
  public static SSMTYPE = "SSMTYPE"       // Documento de producto de tipo Seguro Social Mexicano
  public static CURPTYPE = "CURPTYPE"     // Documento de producto de tipo CURP mexicano

  /** PaymentOptions */
  public static MEMBER_FULLPAYMENT = 1
  public static MEMBER_SPLITPAYMENT = 2
  public static EMPLOYER_FULLPAYMENT = 3
  public static EMPLOYER_SPLITPAYMENT = 4

  /** Estatus del ProductClient durante proceso de pago */
  public static PRODUCT_SOLICITANT = 1
  public static PRODUCT_INVOICE_SENT = 2
  public static PRODUCT_INVOICE_PAYED = 3
  public static PRODUCT_CREDENTIAL_DELIVERED = 4

  /** Transaction Status */
  public static TRANSACTION_IN_DEFINITION = 1
  public static TRANSACTION_PAYMENT_APPROVED = 2
  public static TRANSACTION_PAID_AND_RUNNING = 3
  public static TRANSACTION_FINISHED = 0

  // Dia para INVOICE
  public static PAYMENT_INVOICE_DAY = 15
  public static PAYMENT_TYPE_CUTOFF = 14

  // Finance Charge
  public static FINANCE_CHARGE = 0.01

  // Product1 DiscoutPerDependent
  public static DESC_DEPENDENT_1 = 0.75
  public static DESC_DEPENDENT_2 = 0.65
  public static DESC_DEPENDENT_3 = 0.55
  public static DESC_DEPENDENT_4 = 0.50

  public static GOLD_PRICE_DEP1 = 4.20
  public static GOLD_PRICE_DEP2 = 3.64
  public static GOLD_PRICE_DEP3 = 3.08
  public static GOLD_PRICE_DEP4 = 2.80

  public static SILVER_PRICE_DEP1 = 3.12
  public static SILVER_PRICE_DEP2 = 2.71
  public static SILVER_PRICE_DEP3 = 2.29
  public static SILVER_PRICE_DEP4 = 2.08

  public static SOURCE_WEB = 'Helix'

  /** Valores para opciones de Documento de producto de tipo VISA */
  public static VISATYPE_VALUES= [
    {value: "", caption: "---------"},
    {value: "B1", caption: "Visa B-1 - Business Visitor"},
    {value: "B2", caption: "Visa B-2 - Tourist, vacationers or family visitors"},
    {value: "E2", caption: "Visa E-2 - for Investors"},
    {value: "EB5", caption: "Visa E-B5 - for Immigrant Investors"},
    {value: "H1B", caption: "Visa H-1B - Specialty Labor"},
    {value: "H2A", caption: "Visa H-2A - temporary agriculture worker"},
    {value: "H2B", caption: "Visa H-2B - temporary non-agricultural workers"},
    {value: "H3", caption: "Visa H-3 - trainees in fields other than medical or academic"},
    {value: "L1", caption: "Visa L-1 - intra-company transferees"},
    {value: "O1", caption: "Visa O-1 - individuals with extraordinary ability in sciences, arts, education, business, or athletics"},
    {value: "P1", caption: "Visa P-1 - internationally recognized athletes or members of entertainment groups"},
    {value: "P2", caption: "Visa P-2 - artists or entertainers in a reciprocal exchange program"},
    {value: "Q1", caption: "Visa Q-1 - participants in international cultural exchange programs"},
    {value: "R1", caption: "Visa R-1 - religious workers"},
    {value: "TN/TD", caption: "Visa TN/TD - NAFTA professionals (citizens of Canada and Mexico)"},
    {value: "F1", caption: "Visa F-1 - academic students in schools, colleges, universities, etc."},
    {value: "M1", caption: "Visa M-1 - vocational students in non-academic or vocational programs"},
    {value: "J1", caption: "Visa J-1 - exchange visitors participating in cultural exchange programs"}
  ]

  public static setProductTransaction(inputPT: ProductTransaction): ProductTransaction{
    let result = {} as ProductTransaction
    result.idProductTransaction = inputPT.idProductTransaction
    result.idTransaction = inputPT.idTransaction
    result.idProduct = inputPT.idProduct
    result.contractDate = inputPT.contractDate
    result.startDate = inputPT.startDate
    result.endDate = inputPT.endDate
    result.productActive = inputPT.productActive
    result.coverageDays = inputPT.coverageDays
    result.coverageMonths = inputPT.coverageMonths
    result.status = inputPT.status
    result.coverageCost = inputPT.coverageCost
    result.dependentCoverageCost = inputPT.dependentCoverageCost
    result.paymentOption = inputPT.paymentOption
    result.daysToEOM = inputPT.daysToEOM
    result.includeDependants = inputPT.includeDependants
    result.nextInvoiceToPay = inputPT.nextInvoiceToPay
    return result
  }

  /**
   * Human-readable label for Transaction.transactionStatus and
   * ProductTransaction.status. Mirrors src/domain/lifecycle.js LIFECYCLE.
   */
  public static getTransactionStatusDescription(status:number): string{
    let result = ""
    switch(status){
      case -1: result = "Cancelled";  break
      case 1:  result = "Draft";      break
      case 2:  result = "Invoicing";  break
      case 3:  result = "Active";     break
      case 4:  result = "Completed";  break
    }
    return result
  }

  public static getProductbyId(idProduct: number, products: Product[]){
    let item = products.find(x => x.idProduct == idProduct)
    return item
  }

  public static getSumProducts(products: ProductTransaction[]){
    return products.reduce( (accumulator, item) => accumulator + ( (item.productActive)? (+item.coverageCost) : 0), 0)
  }

  public static getSumCostProducts(calcProducts: ProductCalculateData[]):number{
    return calcProducts.reduce( (accumulator, item) => accumulator + ( (item.calculate)? (+item.calculate.totalCost) : 0), 0)
  }

  public static getSumDependentTotalPrice(calcProducts: ProductCalculateData[]): number{
    return calcProducts.reduce( (accum, item) => accum + ( (item.calculate) ? (+item.calculate.dependentTotalPriceList): 0 ), 0)
  }

  public static getSumDependentTotalCost( calcProducts: ProductCalculateData[]): number{
    return calcProducts.reduce( (accum, item) => accum + ( (item.calculate) ? (+item.calculate.dependentTotalCost) : 0 ), 0 )
  }

  public static getDaysBetweenDates(startDate: Date, endDate: Date){
    startDate.setHours(0,0,0,0)
    endDate.setHours(0,0,0,0)
    return Math.min((endDate.getTime() - startDate.getTime() ) / (1000 * 60 * 60 * 24))
  }

  public static getStatusDescription(status:number){
    let result = ""
    switch(status){
      case this.PRODUCT_SOLICITANT:{
        result = 'Solicitant'
        break
      }
      case this.PRODUCT_INVOICE_SENT:{
        result = 'In payment process'
        break
      }
      case this.PRODUCT_INVOICE_PAYED:{
        result= 'Paid'
        break
      }
    }
    return result
  }

  public static getMaxMinDate(products: ProductTransaction[]){
    let max = '1920-01-01'
    let min = '2100-12-30'
    let maxProduct: ProductTransaction = {} as ProductTransaction
    let minProduct: ProductTransaction = {} as ProductTransaction
    products.forEach(item => {
      if(item.productActive){
        //let itemStartDate = new Date(item.startDate)
        // let itemEndDate = new Date(item.endDate)
        if(item.startDate < min){
          minProduct = item
          min = item.startDate
        }

        if(item.endDate > max){
          maxProduct = item
          max = item.endDate
        }
      }
    })

    return [minProduct, maxProduct]
  }

  public static getDiffMonths(endDate: Date, startDate: Date):number{
    let result = -1
    if(endDate.getTime() > startDate.getTime()){
      let yearMonthEnd = (endDate.getUTCFullYear() * 12) + endDate.getUTCMonth() + 1
      let yearMonthStart = (startDate.getUTCFullYear() * 12) + startDate.getUTCMonth()  + 1
      result = yearMonthEnd - yearMonthStart + 1
    }
    return result
  }

  public static getDaysOfMonth(month: number, year: number): number{
    return new Date(year, month, 0).getDate()
  }

  public static newPayment(): PaymentSubscribe{
    let newPay: PaymentSubscribe = {
      idPayment: 0,
      idTransaction: 0,
      idProductTransaction:0,
      amount:0,
      finalFolioPaymentInstrument:'',
      idTraceAR:'',
      paidAmount: 0,
      paidOn: '0000-00-00',
      paymentDate: '',
      paymentInstrument: '',
      paymentOrder: 0,
      paymentStatus: 1,
      payoutDate: '',
      coverageStartDate:'',
      coverageEndDate:'',
      productTransaction: {} as ProductTransaction,
      invoiceLink: ''
    }
    return newPay
  }

  public static getFirstDateOfPLan(startDate: Date):Date{
    let firstPaymentDate = null
    let today = new Date()
    let tommorrow = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1)
    if(startDate.getTime() <= tommorrow.getTime()){
      return tommorrow
    }else{
      //NumMonths Coverage and months to start
      let monthsToStart = this.getDiffMonths(startDate, tommorrow)


      // Define first Payment Date
      if(monthsToStart <= 2){
        firstPaymentDate = tommorrow
      }else{
        firstPaymentDate = new Date(startDate.getUTCFullYear(), startDate.getUTCMonth() -1, this.PAYMENT_INVOICE_DAY)
      }
    }

    return firstPaymentDate!
  }

  public static definePlanDates(startDate: Date, endDate: Date, locale: string){
    let datesPlan: PaymentSubscribe[] = []
    let firstPaymentDate = null
    let today = new Date()

    let daysToStart = 0

    if(startDate.getTime() <= today.getTime()){
    }else{
      //NumMonths Coverage and months to start
      let totalCoverageMonths = this.getDiffMonths(endDate, startDate)
      let monthsToStart = this.getDiffMonths(startDate, today)

      // Define first Payment Date
      if(monthsToStart <= 2){
        firstPaymentDate = today
      }else{
        firstPaymentDate = new Date(startDate.getFullYear(), startDate.getMonth() -1, this.PAYMENT_INVOICE_DAY)
      }

      // Define Payments Plan
      let firstPayment = this.newPayment()
      firstPayment.paymentOrder = 1
      firstPayment.paymentDate = formatDate(firstPaymentDate, 'yyyy-MM-dd', locale)
      datesPlan.push(firstPayment)
      for(let i=1; i < totalCoverageMonths; i++){
        let newItem = this.newPayment()
        let newDate =new Date(firstPaymentDate.getFullYear(), firstPaymentDate.getMonth() + i, this.PAYMENT_INVOICE_DAY)
        newItem.paymentOrder = i + 1
        newItem.paymentDate = formatDate(newDate, 'yyyy-MM-dd', locale)
        datesPlan.push(newItem)
      }
    }
    let result = {
      firstPaymentDate: firstPaymentDate,
      datePlan: datesPlan
    }

    return result
  }

  public static  defineSplitPaymentPlan(totalPriceList: number, productTransaction: ProductTransaction[], prodArray:Product[], locale: string):PaymentSubscribe[]{
    let datesPlan: PaymentSubscribe[] = []
    let prod1 = prodArray.find(x => x.idProduct == this.PROD_ISOIMG_ID)
    let prod2 = prodArray.find(x => x.idProduct == this.PROD_SSM_ID)
    let prod3 = prodArray.find(x => x.idProduct == this.PROD_TM_ID)
    let prod4 = prodArray.find(x => x.idProduct == this.PROD_LN_ID)

    let idx = [0, 0, 0, 0]
    let totalPerProduct = [0, 0, 0, 0]

    let prodImgIso = productTransaction.find(pt => pt.idProduct == this.PROD_ISOIMG_ID && pt.productActive)
    let prodSSM = productTransaction.find(pt => pt.idProduct == this.PROD_SSM_ID && pt.productActive)
    let prodTM = productTransaction.find(pt => pt.idProduct == this.PROD_TM_ID && pt.productActive)
    let prodLN = productTransaction.find(pt => pt.idProduct == this.PROD_LN_ID && pt.productActive)

    let prod1_startDate = (prodImgIso)? new Date(formatDate(prodImgIso!.startDate, 'yyyy-MM-dd', locale)) : null
    let prod1_endDate = (prodImgIso)? new Date(formatDate(prodImgIso!.endDate, 'yyyy-MM-dd', locale)): null

    let prod2_startDate = (prodSSM)? new Date(formatDate(prodSSM!.startDate, 'yyyy-MM-dd', locale)): null
    let prod2_endtDate = (prodSSM)? new Date(formatDate(prodSSM!.endDate, 'yyyy-MM-dd', locale)): null

    let prod3_startDate = (prodTM)? new Date(formatDate(prodTM!.startDate, 'yyyy-MM-dd', locale)): null
    let prod3_endtDate = (prodTM)? new Date(formatDate(prodTM!.endDate, 'yyyy-MM-dd', locale)): null

    let prod4_startDate = (prodLN)? new Date(formatDate(prodLN!.startDate, 'yyyy-MM-dd', locale)): null
    let prod4_endtDate = (prodLN)? new Date(formatDate(prodLN!.endDate, 'yyyy-MM-dd', locale)): null

    let totalProductosAsignado = 0
    let ciclos = 0

    let maxMinDate = this.getMaxMinDate(productTransaction)
    let firstPaymentDate = this.getFirstDateOfPLan(new Date(formatDate(maxMinDate[0].startDate, 'yyyy-MM-dd', locale)))

    while(totalProductosAsignado < totalPriceList){
      let mensualidad = this.newPayment()

      // Define paymentDate
      if(ciclos == 0){
        mensualidad.paymentOrder = 1
        mensualidad.paymentDate = formatDate(firstPaymentDate, 'yyyy-MM-dd', locale)
      }else{
        let nextPaymentDate = new Date(firstPaymentDate.getFullYear(), firstPaymentDate.getMonth() + ciclos + 1, this.PAYMENT_INVOICE_DAY)
        mensualidad.paymentOrder = ciclos + 1
        mensualidad.paymentDate = formatDate(nextPaymentDate, 'yyyy-MM-dd', locale)
      }

      let payDate = new Date(formatDate(mensualidad.paymentDate, 'yyyy-MM-dd', locale))
      let montoMensual = 0

      //prodIMGISO calc (idProduct == 1)
      if(prodImgIso && prod1_startDate && prod1_endDate && (payDate.valueOf() >= prod1_startDate.valueOf() && payDate.valueOf() <= prod1_endDate.valueOf())){

        let mensualidadProd1 = 30.5 * prod1?.priceList!
        let montoTotalPagar = prodImgIso?.coverageCost! + prodTM?.coverageCost!
        if(idx[0] == 0){ // Primer mensualiad
          let endOfMonth = new Date(payDate.getFullYear(), payDate.getMonth() + 1, 0)
          let daysToEOM = Math.min((prod1_startDate.getTime() - endOfMonth.getTime()) / 1000 * 60 * 60 * 24)
          let partialDaysAmount = (daysToEOM * prod1?.priceList!) + prod3?.priceList!
          if(partialDaysAmount + mensualidadProd1 <= prodImgIso.coverageCost + prodTM?.coverageCost!){ //pago parcial mas primera mensualidad es menor al total a pagar
            montoMensual += partialDaysAmount + mensualidadProd1
            totalPerProduct[this.PROD_ISOIMG_ID - 1] = partialDaysAmount + (30.5 * prod1?.priceList!)
            totalPerProduct[this.PROD_TM_ID - 1] = prod3?.priceList! * 2
          }else{
            montoMensual += montoTotalPagar
            totalPerProduct[this.PROD_ISOIMG_ID - 1] += prodImgIso?.coverageCost!
            totalPerProduct[this.PROD_TM_ID - 1] += prodTM?.coverageCost!
          }
        }else{ // No es la primera mensualidad
          let montoAsignadoPlan = totalPerProduct[this.PROD_ISOIMG_ID - 1] + totalPerProduct[this.PROD_TM_ID - 1]
          if(montoAsignadoPlan + mensualidadProd1 < (prodImgIso.coverageCost + prodTM?.coverageCost!)){  // Lo pendiente no sobrepasa el costo total
            montoMensual += mensualidadProd1
            totalPerProduct[this.PROD_ISOIMG_ID - 1] += mensualidadProd1
            totalPerProduct[this.PROD_TM_ID - 1] += prod3?.priceList!
          }else{
            let totalAsignado = totalPerProduct[this.PROD_ISOIMG_ID - 1] + totalPerProduct[this.PROD_TM_ID - 1]
            let pendientePorAsignar = montoTotalPagar - totalAsignado
            montoMensual += pendientePorAsignar
          }
        }
      }

      // prodSSM calc (idProduct == 2)
      if(prodSSM && prod2_startDate && prod2_endtDate && (payDate.valueOf() >= prod2_startDate.valueOf() && payDate.valueOf() <= prod2_endtDate.valueOf())){
        montoMensual += prod2?.priceList!
        totalPerProduct[this.PROD_SSM_ID - 1] += prod2?.priceList!
      }

      // prodTM calc (idProduct == 3)
      if((prodTM && !prodImgIso) && prod3_startDate && prod3_endtDate && (payDate.valueOf() >= prod3_startDate.valueOf() && payDate.valueOf() <= prod3_endtDate.valueOf())){
        montoMensual += prod3?.priceList!
        totalPerProduct[this.PROD_TM_ID - 1] += prod2?.priceList!
      }

      // prodLN calc (idProduct ==4)
      if(prodLN && prod4_startDate && prod4_endtDate && (payDate.valueOf() >= prod4_startDate.valueOf() && payDate.valueOf() <= prod4_endtDate.valueOf())){
        montoMensual += prod4?.priceList!
        totalPerProduct[this.PROD_LN_ID - 1] += prod4?.priceList!
      }
      mensualidad.amount = montoMensual
      totalProductosAsignado += montoMensual

      datesPlan.push(mensualidad)

      ciclos += 1
    }

    return datesPlan
  }

  public static getPaymentInfo(transaction: Transaction, locale: string): PaymentInfo{
    let paymentInfo: PaymentInfo = {} as PaymentInfo
    let planDates = this.definePlanDates(new Date(transaction.transactionStartDate), new Date(transaction.transactionEndDate), locale)
    paymentInfo.paymentMethod = transaction.paymentMethod
    paymentInfo.payments = new Array()

    if( transaction.paymentMethod == this.EMPLOYER_FULLPAYMENT || transaction.paymentMethod == this.MEMBER_FULLPAYMENT ){ // full payment
      paymentInfo.firstPaymentAmount = this.getSumProducts(transaction.productTransaction)
      paymentInfo.firstPaymentDate = new Date()
      paymentInfo.monthlyPaymentAmount = 0
      paymentInfo.nextPaymentDate = new Date()
      paymentInfo.totalPayment = paymentInfo.firstPaymentAmount
      paymentInfo.payments = [
        {
          idPayment:0,
          idTransaction: transaction.idTransaction,
          idProductTransaction:0,
          paymentOrder: 1,
          idTraceAR:'',
          paymentDate: formatDate(new Date(), 'yyyy-MM-dd', locale),
          amount: paymentInfo.totalPayment,
          paymentStatus: ProductUtils.PRODUCT_SOLICITANT,
          payoutDate:'0000-00-00',
          paidOn:'000-00-00',
          paidAmount: 0,
          paymentInstrument:'',
          finalFolioPaymentInstrument:'',
          coverageStartDate: formatDate(transaction.transactionStartDate, 'yyyy-MM-dd', locale),
          coverageEndDate: formatDate(transaction.transactionEndDate, 'yyyy-MM-dd', locale),
          productTransaction: {} as ProductTransaction,
          invoiceLink: ''
        }
      ]
    }

    if(transaction.paymentMethod == this.EMPLOYER_SPLITPAYMENT || transaction.paymentMethod == this.MEMBER_SPLITPAYMENT){ // split payment
      let prod1 = transaction.productTransaction.find(x => x.idProduct == this.PROD_ISOIMG_ID )
      let prod2 = transaction.productTransaction.find(x => x.idProduct == this.PROD_SSM_ID )
      let prod3 = transaction.productTransaction.find(x => x.idProduct == this.PROD_TM_ID )
      let prod4 = transaction.productTransaction.find(x => x.idProduct == this.PROD_LN_ID )

      let idx1 = 0
      let idx2 = 0
      let idx3 = 0
      let idx4 = 0

      paymentInfo.financeChargeRate =  0.7
      paymentInfo.totalCoverageMonths


      planDates.datePlan.forEach(itemPlan =>{

      })


      /*
      let maxMinProducts = this.getMaxMinDate(products)
      let today = formatDate(new Date(), 'yyyy-MM-dd', locale)
      let daysToStart = DateUtils.daysBetween(today, maxMinProducts[0].startDate)

      paymentInfo.firstPaymentDate = DateUtils.getFirstDatePayment(maxMinProducts[0].startDate)
      paymentInfo.totalCoverageMonths = DateUtils.datesMonthsDiff(maxMinProducts[0].startDate, maxMinProducts[1].endDate)
      paymentInfo.totalPayment = this.getSumProducts(products)
      paymentInfo.monthlyPaymentAmount = paymentInfo.totalPayment / paymentInfo.totalCoverageMonths
      paymentInfo.nextPaymentDate = DateUtils.getNextDatePayment(paymentInfo.firstPaymentDate)
      paymentInfo.firstPaymentAmount = paymentInfo.monthlyPaymentAmount
      paymentInfo.payments = [
        {
          idPayment:0,
          idTransaction: idTransaction,
          paymentOrder: 1,
          idTraceAR:'',
          paymentDate: formatDate(paymentInfo.firstPaymentDate, 'yyyy-MM-dd', locale),
          amount:paymentInfo.monthlyPaymentAmount,
          paymentStatus: ProductUtils.PRODUCT_SOLICITANT,
          payoutDate: '',
          paidAmount: 0,
          paymentInstrument:'',
          finalFolioPaymentInstrument:''
        }
      ]
      for(let i = 1; i < paymentInfo.totalCoverageMonths; i++){
        paymentInfo.payments.push(
        {
          idPayment:0,
          idTransaction: idTransaction,
          paymentOrder: i + 1,
          idTraceAR:'',
          paymentDate: formatDate(DateUtils.getNextDatePayment(new Date(paymentInfo.payments[i-1].paymentDate)), 'yyyy-MM-dd', locale),
          amount: paymentInfo.monthlyPaymentAmount,
          paymentStatus: ProductUtils.PRODUCT_SOLICITANT,
          payoutDate: '',
          paidAmount: 0,
          paymentInstrument:'',
          finalFolioPaymentInstrument:''
        })
      }
      */
    }
    return paymentInfo
  }

  public static getPaymentMethod(responsible: string, frequency: string): number{
    let result = 0

    if(responsible == 'member'){
      if(frequency == 'full'){
        result = this.MEMBER_FULLPAYMENT
      }
      if(frequency == 'split'){
        result = this.MEMBER_SPLITPAYMENT
      }
    }
    if(responsible == 'employer'){
      if(frequency == 'full'){
        result = this.EMPLOYER_FULLPAYMENT
      }
      if(frequency == 'split'){
        result = this.EMPLOYER_SPLITPAYMENT
      }
    }
    return result
  }

  public static getFullPaymentMethod(paymentMethod: number){
    let result = ""
    switch(paymentMethod){
      case this.MEMBER_FULLPAYMENT:{
        result = 'Benefficiary pay in one exhibition'
        break
      }
      case this.MEMBER_SPLITPAYMENT:{
        result = 'Benefficiary pay monthly'
        break
      }
      case this.EMPLOYER_SPLITPAYMENT:{
        result = 'Employer pay monthly'
        break
      }
    }
    return result
  }

  public static countActiveProducts(products:ProductTransaction[]):number{
    let result: number = 0
    result = products.filter(x => x.productActive == true).length
    return result
  }


  public static productShortName(idProduct:number):string{
    let result = ""
    switch(idProduct){
      case this.PROD_ISOIMG_ID:{
        result = "IMG"
        break
      }
      case this.PROD_SSM_ID:{
        result = "IMSS"
        break
      }
      case this.PROD_TM_ID:{
        result = "ISO"
        break
      }
      case this.PROD_LN_ID:{
        result = "LegalNexus"
        break
      }
    }
    return result
  }

  public static countProducts(transactions: Transaction[], products: Product[]):CountProductsData[]{
    let imgiso: CountProductsData = {} as CountProductsData
    let ssm: CountProductsData = {} as CountProductsData
    let tm: CountProductsData = {} as CountProductsData
    let ln: CountProductsData = {} as CountProductsData

    imgiso.idProduct = this.PROD_ISOIMG_ID
    imgiso.productName = products.find(x => x.idProduct == this.PROD_ISOIMG_ID)?.productName!
    imgiso.shortName = products.find(x => x.idProduct == this.PROD_ISOIMG_ID)?.shortName!
    imgiso.count = 0
    imgiso.total = 0
    imgiso.total_status1 = 0
    imgiso.total_status2 = 0
    imgiso.total_status3 = 0

    ssm.idProduct = this.PROD_SSM_ID
    ssm.productName = products.find(x => x.idProduct == this.PROD_SSM_ID)?.productName!
    ssm.shortName = products.find(x => x.idProduct == this.PROD_SSM_ID)?.shortName!
    ssm.count = 0
    ssm.total = 0
    ssm.total_status1 = 0
    ssm.total_status2 = 0
    ssm.total_status3 = 0

    tm.idProduct = this.PROD_TM_ID
    tm.productName = products.find(x => x.idProduct == this.PROD_TM_ID)?.productName!
    tm.shortName = products.find(x => x.idProduct == this.PROD_TM_ID)?.shortName!
    tm.count = 0
    tm.total = 0
    tm.total_status1 = 0
    tm.total_status2 = 0
    tm.total_status3 = 0

    ln.idProduct = this.PROD_LN_ID
    ln.productName = products.find(x => x.idProduct == this.PROD_LN_ID)?.productName!
    ln.shortName = products.find(x => x.idProduct == this.PROD_LN_ID)?.shortName!
    ln.count = 0
    ln.total = 0
    ln.total_status1 = 0
    ln.total_status2 = 0
    ln.total_status3 = 0

    transactions.forEach(item =>{
      item.productTransaction.forEach(prod => {
        if(prod.productActive){
          switch(prod.idProduct){
            case this.PROD_ISOIMG_ID:{
              imgiso.count += 1
              imgiso.total += +prod.coverageCost
              imgiso.total_status1 += (item.transactionStatus == this.PRODUCT_SOLICITANT) ? +prod.coverageCost : 0
              imgiso.total_status2 += (item.transactionStatus == this.PRODUCT_INVOICE_SENT) ? +prod.coverageCost : 0
              imgiso.total_status3 += (item.transactionStatus == this.PRODUCT_INVOICE_PAYED) ? +prod.coverageCost : 0
              break
            }
            case this.PROD_SSM_ID:{
              ssm.count += 1
              ssm.total += +prod.coverageCost
              ssm.total_status1 += (item.transactionStatus == this.PRODUCT_SOLICITANT) ? +prod.coverageCost : 0
              ssm.total_status2 += (item.transactionStatus == this.PRODUCT_INVOICE_SENT) ? +prod.coverageCost : 0
              ssm.total_status3 += (item.transactionStatus == this.PRODUCT_INVOICE_PAYED) ? +prod.coverageCost : 0
              break
            }
            case this.PROD_TM_ID:{
              tm.count += 1
              tm.total += +prod.coverageCost
              tm.total_status1 += (item.transactionStatus == this.PRODUCT_SOLICITANT) ? +prod.coverageCost : 0
              tm.total_status2 += (item.transactionStatus == this.PRODUCT_INVOICE_SENT) ? +prod.coverageCost : 0
              tm.total_status3 += (item.transactionStatus == this.PRODUCT_INVOICE_PAYED) ? +prod.coverageCost : 0
              break
            }
            case this.PROD_LN_ID:{
              ln.count += 1
              ln.total += +prod.coverageCost
              ln.total_status1 += (item.transactionStatus == this.PRODUCT_SOLICITANT) ? +prod.coverageCost : 0
              ln.total_status2 += (item.transactionStatus == this.PRODUCT_INVOICE_SENT) ? +prod.coverageCost : 0
              ln.total_status3 += (item.transactionStatus == this.PRODUCT_INVOICE_PAYED) ? +prod.coverageCost : 0
              break
            }
          }
        }
      })
    })
    return [imgiso, ssm, tm, ln]
  }

  public static calculateDependentPrice(daysToCover: number, numDependents: number, financeChargeRate: number, isGold: boolean):number{
    let result = 0

    let basePrice = daysToCover * (financeChargeRate)
    if(numDependents >= 1){
      result += basePrice * ((isGold == true)? this.GOLD_PRICE_DEP1 : this.SILVER_PRICE_DEP1)
    }

    if(numDependents >= 2){
      result += basePrice * ((isGold == true)? this.GOLD_PRICE_DEP2 : this.SILVER_PRICE_DEP2)
    }

    if(numDependents >= 3){
      result += basePrice * ((isGold == true)? this.GOLD_PRICE_DEP3 : this.SILVER_PRICE_DEP3)
    }

    if(numDependents >= 4){
      result += basePrice * (numDependents - 3) * ((isGold == true)? this.GOLD_PRICE_DEP4 : this.SILVER_PRICE_DEP4)
    }

    return result
  }

  public static differenceMonths(startDate: Date, endDate: Date):number{
    let result = 0
    if(startDate.getTime() < endDate.getTime()){
      let yearMonthEnd = (endDate.getUTCFullYear() * 12) + endDate.getUTCMonth() + 1
      let yearMonthStart = (startDate.getUTCFullYear() * 12) + startDate.getUTCMonth() + 1
      result = yearMonthEnd - yearMonthStart
    }
    return result
  }

  public static hayDiaPagoEntreFechas(startDate: Date, endDate: Date):boolean{
    let result = false

    if(startDate.getTime() < endDate.getTime()){
      let diffMonth = this.differenceMonths(startDate, endDate)
      if(diffMonth > 0){
        if(diffMonth > 1){
          result = true
        }else{
          result = startDate.getDate() < ProductUtils.PAYMENT_INVOICE_DAY || endDate.getDate() > ProductUtils.PAYMENT_INVOICE_DAY
        }
      }
    }

    return result
  }


  /**
   *
   * @param transaction
   * @param productTransaction
   * @param products
   * @param dependents
   * @param locale
   * @returns
   */
  public static productCalculate(transaction:Transaction, productTransaction: ProductTransaction[], products: Product[], dependents: Dependent[], locale: string){
    let errorMsg: string[] = []
    let prodImgIso = productTransaction.find(x => x.idProduct == this.PROD_ISOIMG_ID && x.productActive == true)
    let prodSSM = productTransaction.find(x => x.idProduct == this.PROD_SSM_ID && x.productActive == true )
    let prodTM = productTransaction.find(x => x.idProduct == this.PROD_TM_ID && x.productActive == true)
    let prodLN = productTransaction.find(x => x.idProduct == this.PROD_LN_ID && x.productActive == true)

    let prodImgIsoPlan: PaymentSubscribe[] = []
    let prodSSMPlan: PaymentSubscribe[] = []
    let prodTMPlan: PaymentSubscribe[] = []
    let prodLNPLan: PaymentSubscribe[] = []

    let prod1 = products.find(x => x.idProduct == this.PROD_ISOIMG_ID)
    let prod2 = products.find(x => x.idProduct == this.PROD_SSM_ID)
    let prod3 = products.find(x => x.idProduct == this.PROD_TM_ID)
    let prod4 = products.find(x => x.idProduct == this.PROD_LN_ID)
    let prod5 = products.find(x => x.idProduct == this.PROD_ISOIMGGOLD_ID)

    let today = new Date( new Date().getFullYear(), new Date().getMonth(), new Date().getDate(),0,0,0,0 ) // date without hours
    let totalPrice = 0
    let totalCost = 0

    let totalFirstPayment = 0

    // getMaxMinDates Coverage
    if((prodImgIso && prodImgIso.productActive) || (prodSSM && prodSSM.productActive) || (prodTM && prodTM.productActive) || (prodLN && prodLN.productActive)){
      let maxMinDates = this.getMaxMinDate(productTransaction)
      transaction.transactionStartDate = maxMinDates[0].startDate
      transaction.transactionEndDate = maxMinDates[1].endDate
      transaction.coverageDays = this.getDaysBetweenDates(new Date(formatDate( transaction.transactionStartDate,'yyyy-MM-dd', locale)), new Date(formatDate( transaction.transactionEndDate, 'yyyy-MM-dd', locale)))
      transaction.coverageMonths = this.getDiffMonths(new Date(formatDate(transaction.transactionEndDate, 'yyyy-MM-dd', locale)), new Date(formatDate(transaction.transactionStartDate, 'yyyy-MM-dd', locale)))
      transaction.numDependents = dependents.length
    }



    let financeChargeRate = ([this.EMPLOYER_FULLPAYMENT, this.MEMBER_FULLPAYMENT]).includes(transaction.paymentMethod) ? 1 : 1 + this.FINANCE_CHARGE

    // Calculate IMG ISO
    if(prodImgIso && prodImgIso.productActive){
      let startDate = new Date( prodImgIso.startDate+'T00:00:00')
      let endDate = new Date( prodImgIso.endDate )
      let coverageMonths = 0

      if(startDate.getTime() < endDate.getTime()){
        let diffMonthsTodayStartDate = this.differenceMonths(today, startDate)
        let thereArePaymentDay = this.hayDiaPagoEntreFechas(today, startDate)
        let firstEOM = null
        let basePaymentDate = null

        // Condición pago parcial o parcial más 1 mes. Si hay fecha de pago en medio y son diferentes meses, pago es parcial.
        if(diffMonthsTodayStartDate > 0){                      // solo se cobre parcial
          firstEOM = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0)
          basePaymentDate = new Date(startDate.getFullYear(), startDate.getMonth(), this.PAYMENT_INVOICE_DAY)
        }else{
          if(startDate.getDate() <= this.PAYMENT_TYPE_CUTOFF){  // Solo se cobra parcial
            firstEOM = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0)
            basePaymentDate = new Date(startDate.getFullYear(), startDate.getMonth(), this.PAYMENT_INVOICE_DAY)
          }else{                                                // Se cobra parcial más siguiente mes
            firstEOM = new Date(startDate.getFullYear(), startDate.getMonth() + 2, 0)
            basePaymentDate = new Date(startDate.getFullYear(), startDate.getMonth() + 1, this.PAYMENT_INVOICE_DAY)
          }
        }

        //let depCoverage: number[] = []

        prodImgIso.daysToEOM = this.getDaysBetweenDates(startDate, firstEOM)
        prodImgIso.coverageDays = this.getDaysBetweenDates(startDate, endDate)


        if(prodImgIso.coverageDays <= 10){
          errorMsg.push('IMGISO: Coverage days is less than 10 days. ')
          endDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + 10)
          prodImgIso.coverageDays = prodImgIso.coverageDays = this.getDaysBetweenDates(startDate, endDate)
        }

        prodImgIso.coverageMonths = this.differenceMonths(startDate, endDate)

        // check if day of start date is before 20th day of month
        /*
        if(startDate.getDate() > 20){
          transaction.coverageMonths -= 1
          prodImgIso.coverageMonths -= 1
        }
        */

        prodImgIso.productName = prod1?.productName!
        prodImgIso.paymentOption = transaction.paymentMethod
        prodImgIso.product = prod1!
        prodImgIso.coverageStartDate = new Date( prodImgIso.startDate+'T00:00:00')
        let baseProductPrice = (prodImgIso.idProduct === 1)? prod1?.priceList! : prod5?.priceList!
        let baseProductCost = (prodImgIso.idProduct === 1)? prod1?.costProduct! : prod5?.costProduct!
        let priceList = (prodImgIso.coverageDays * baseProductPrice * financeChargeRate)
        let isGold = prodImgIso.idProduct !== 1


        // Calculate Dependent Coverage price
        transaction.dependentTotalPrice = this.calculateDependentPrice(prodImgIso.coverageDays, dependents.length, financeChargeRate, isGold)


        prodImgIso.coverageCost = priceList + transaction.dependentTotalPrice // Ya se incluye el costo financiero

        transaction.dependentTotalCost = baseProductCost * prodImgIso.coverageDays * dependents.length

        // add product price to transaction priceList
        totalPrice += prodImgIso.coverageCost
        totalCost += baseProductCost * prodImgIso.coverageDays * (1 + dependents.length)

        // Define Payments Plan
        let firstPayment: PaymentSubscribe = {} as PaymentSubscribe
        prodImgIso.payments = []
        let dateOfFirstPayment = this.getFirstDateOfPLan(startDate)

        if([this.MEMBER_SPLITPAYMENT, this.EMPLOYER_SPLITPAYMENT].includes(transaction.paymentMethod)){ // IMGISO en pagos mensuales

          // Revisar si fecha primer pago es antes del 15 previo al primer mes
          let firstDuePayment = new Date(startDate.getFullYear(), startDate.getMonth() -1 , 15, 0, 0, 0) // dia 15 del mes anterior

          let daysProcessed = 0 // dias procesados o cubiertos por un pago

          // Valores de Primer Pago
          let daysFirstPayment = 0
          firstPayment.idProductTransaction = prodImgIso.idProductTransaction
          firstPayment.paymentDate = formatDate(new Date(), 'yyyy-MM-dd', locale)
          firstPayment.paymentOrder = 1
          firstPayment.paymentStatus = this.PRODUCT_SOLICITANT
          daysFirstPayment = prodImgIso.daysToEOM  // dias cubiertos por el primer pago. Ver condición de pago parcial o mensualidad completa

          let basePrice = daysFirstPayment * baseProductPrice * financeChargeRate

          let dependentsFirstMonth = this.calculateDependentPrice(daysFirstPayment, dependents.length,financeChargeRate, isGold)
          firstPayment.amount = basePrice + dependentsFirstMonth
          prodImgIsoPlan.push(firstPayment)
          daysProcessed += daysFirstPayment
          let totalAmountProcessed = 0
          totalAmountProcessed = firstPayment.amount

          // basePaymentDate = new Date(startDate.getFullYear(), startDate.getMonth(), this.PAYMENT_INVOICE_DAY) //
          for(let i = 1; daysProcessed < prodImgIso.coverageDays; i++){
            let numDaysMonth = this.getDaysOfMonth(basePaymentDate.getMonth() + i + 1 , basePaymentDate.getFullYear())
            let daysToProcess = ( (daysProcessed + numDaysMonth) > prodImgIso.coverageDays)? prodImgIso.coverageDays - daysProcessed: numDaysMonth
            let newPayment: PaymentSubscribe = {} as PaymentSubscribe
            let newBasePrice = daysToProcess * baseProductPrice * financeChargeRate
            let depsPrice = this.calculateDependentPrice(daysToProcess, dependents.length, financeChargeRate, isGold)

            newPayment.idProductTransaction = prodImgIso.idProductTransaction
            newPayment.paymentDate = formatDate( new Date(basePaymentDate.getFullYear(), basePaymentDate.getMonth() + i - 1, this.PAYMENT_INVOICE_DAY), 'yyyy-MM-dd', locale )
            newPayment.amount = newBasePrice + depsPrice
            newPayment.paymentOrder = i + 1
            newPayment.paymentStatus = this.PRODUCT_SOLICITANT
            prodImgIsoPlan.push(newPayment)
            totalAmountProcessed += firstPayment.amount
            daysProcessed += daysToProcess
          }

        }
        if([this.EMPLOYER_FULLPAYMENT, this.MEMBER_FULLPAYMENT].includes(transaction.paymentMethod)){
          firstPayment.idProductTransaction =prodImgIso.idProductTransaction
          firstPayment.paymentDate = formatDate(new Date(), 'yyyy-MM-dd', locale)
          firstPayment.amount = prodImgIso.coverageCost
          firstPayment.paymentOrder = 1
          firstPayment.paymentStatus = this.PRODUCT_SOLICITANT
          prodImgIsoPlan.push(firstPayment)
        }
        totalFirstPayment += firstPayment.amount
      }else{
        errorMsg.push('IMGISO: Start date is after of end date.')
      }
    }

    // Calculate Social Security
    if(prodSSM && prodSSM.productActive){
      let startDate = new Date( formatDate(prodSSM.startDate, 'yyyy-MM-dd', locale) + 'T00:00:00' )
      let endDate = new Date( formatDate(prodSSM.endDate, 'yyyy-MM-dd', locale) + 'T00:00:00' )

      let coverageMonths = this.getDiffMonths(endDate, startDate)
      if(coverageMonths > 1){
        prodSSM.coverageMonths = coverageMonths
        prodSSM.paymentOption = transaction.paymentMethod
        prodSSM.coverageStartDate = startDate
        prodSSM.coverageDays = 0
        prodSSM.daysToEOM = 0

        //Define Payment Plan
        let firstPaymentDate =  this.getFirstDateOfPLan(startDate)
        let firstPayment: PaymentSubscribe = {} as PaymentSubscribe

        if([this.MEMBER_SPLITPAYMENT, this.EMPLOYER_SPLITPAYMENT].includes(transaction.paymentMethod)){
          prodSSM.coverageCost = prod2?.priceList! * prodSSM.coverageMonths * financeChargeRate

          // add product price to transaction priceList
          totalPrice += prodSSM.coverageCost
          totalCost += prod2?.costProduct! * prodSSM.coverageMonths

          firstPayment.idProductTransaction = prodSSM.idProductTransaction
          firstPayment.paymentDate = formatDate(firstPaymentDate, 'yyyy-MM-dd', locale)
          firstPayment.amount = prod2?.priceList! * financeChargeRate
          firstPayment.paymentOrder = 1
          firstPayment.paymentStatus = this.PRODUCT_SOLICITANT
          prodSSMPlan.push(firstPayment)
          for(let i = 1; i < prodSSM.coverageMonths; i++){
            let newPayment: PaymentSubscribe = {} as PaymentSubscribe
            newPayment.idProductTransaction = prodSSM.idProductTransaction
            newPayment.paymentDate = formatDate( new Date(firstPaymentDate.getFullYear(), firstPaymentDate.getMonth() + i, this.PAYMENT_INVOICE_DAY), 'yyyy-MM-dd', locale)
            newPayment.amount = prod2?.priceList! * financeChargeRate
            newPayment.paymentOrder = i + 1
            newPayment.paymentStatus = this.PRODUCT_SOLICITANT
            prodSSMPlan.push(newPayment)
          }
        }

        if([this.MEMBER_FULLPAYMENT, this.EMPLOYER_FULLPAYMENT].includes(transaction.paymentMethod)){
          prodSSM.coverageCost = prod2?.priceList! * prodSSM.coverageMonths

          // add product price to transaction priceList
          totalPrice += prodSSM.coverageCost
          totalCost += prod2?.costProduct! * prodSSM.coverageMonths

          firstPayment.idProductTransaction = prodSSM.idProductTransaction
          firstPayment.paymentDate = formatDate(firstPaymentDate, 'yyyy-MM-dd', locale)
          firstPayment.amount = prodSSM.coverageCost
          firstPayment.paymentOrder = 1
          firstPayment.paymentStatus = this.PRODUCT_SOLICITANT
          prodSSMPlan.push(firstPayment)
        }

        totalFirstPayment += firstPayment.amount
      }else{
        errorMsg.push('SSM: Error in the Coverage Period. Review start and end dates.')
      }
    }

    // Calculate Telemedicine
    if(prodTM && prodTM.productActive && !(prodImgIso && prodImgIso.productActive)){
      let startDate = new Date( formatDate(prodTM.startDate, 'yyyy-MM-dd', locale) )
      let endDate = new Date( formatDate(prodTM.endDate, 'yyyy-MM-dd', locale) )

      let coverageMonths = this.getDiffMonths(endDate, startDate)
      if(coverageMonths > 1){
        prodTM.coverageMonths = coverageMonths
        prodTM.paymentOption = transaction.paymentMethod
        prodTM.coverageStartDate = startDate
        prodTM.coverageDays = 0
        prodTM.daysToEOM = 0

        //Define Payment Plan
        let firstPaymentDate =  this.getFirstDateOfPLan(new Date(formatDate(prodTM.startDate, 'yyyy-MM-dd', locale)))
        let firstPayment: PaymentSubscribe = {} as PaymentSubscribe

        if([this.MEMBER_SPLITPAYMENT, this.EMPLOYER_SPLITPAYMENT].includes(transaction.paymentMethod)){
          prodTM.coverageCost = prod3?.priceList! * prodTM.coverageMonths * financeChargeRate

          // add product price to transaction priceList
          totalPrice += prodTM.coverageCost
          totalCost += prod3?.costProduct! * prodTM.coverageMonths

          firstPayment.idProductTransaction = prodTM.idProductTransaction
          firstPayment.paymentDate = formatDate(firstPaymentDate, 'yyyy-MM-dd', locale)

          let addMonths = 0
          if(formatDate(firstPaymentDate, 'yyyy-mm', locale) == formatDate(new Date(), 'yyyy-mm', locale)){
            firstPayment.amount = prod3?.priceList! * financeChargeRate * 2
            addMonths = 1
          } else{
            firstPayment.amount = prod3?.priceList! * financeChargeRate
          }



          firstPayment.paymentOrder = 1
          firstPayment.paymentStatus = this.PRODUCT_SOLICITANT
          prodTMPlan.push(firstPayment)

          for(let i = 1 + addMonths; i < prodTM.coverageMonths; i++){
            let newPayment: PaymentSubscribe = {} as PaymentSubscribe
            newPayment.idProductTransaction = prodTM.idProductTransaction
            newPayment.paymentDate = formatDate( new Date(firstPaymentDate.getFullYear(), firstPaymentDate.getMonth() + i, this.PAYMENT_INVOICE_DAY), 'yyyy-MM-dd', locale)
            newPayment.amount = prod3?.priceList! * financeChargeRate
            newPayment.paymentOrder = i + 1 - addMonths
            newPayment.paymentStatus = this.PRODUCT_SOLICITANT
            prodTMPlan.push(newPayment)
          }
        }

        if([this.MEMBER_FULLPAYMENT, this.EMPLOYER_FULLPAYMENT].includes(transaction.paymentMethod)){
          prodTM.coverageCost = prod3?.priceList! * prodTM.coverageMonths

          // add product price to transaction priceList
          totalPrice += prodTM.coverageCost
          totalCost += prod3?.costProduct! * prodTM.coverageMonths

          firstPayment.idProductTransaction = prodTM.idProductTransaction
          firstPayment.paymentDate = formatDate(firstPaymentDate, 'yyyy-MM-dd', locale)
          firstPayment.amount = prodTM.coverageCost
          firstPayment.paymentOrder = 1
          firstPayment.paymentStatus = this.PRODUCT_SOLICITANT
          prodTMPlan.push(firstPayment)
        }
        totalFirstPayment += firstPayment.amount
      }
    }

    // Calculate LegalNexus
    if(prodLN && prodLN.productActive){
      let startDate = new Date( formatDate(prodLN.startDate, 'yyyy-MM-dd', locale) )
      let endDate = new Date( formatDate(prodLN.endDate, 'yyyy-MM-dd', locale) )
      let coverageMonths = this.getDiffMonths(endDate, startDate)
      if(coverageMonths > 1){
        prodLN.coverageMonths = coverageMonths
        prodLN.coverageDays = 0
        prodLN.paymentOption = transaction.paymentMethod
        prodLN.coverageStartDate = startDate


        //Define Payment Plan
        let firstPaymentDate =  this.getFirstDateOfPLan(new Date(formatDate(prodLN.startDate, 'yyyy-MM-dd', locale)))
        let firstPayment: PaymentSubscribe = {} as PaymentSubscribe

        if([this.MEMBER_SPLITPAYMENT, this.EMPLOYER_SPLITPAYMENT].includes(transaction.paymentMethod)){
          prodLN.coverageCost = prod4?.priceList! * prodLN.coverageMonths * financeChargeRate
          prodLN.daysToEOM = 0

          // add product price to transaction priceList
          totalPrice += prodLN.coverageCost
          totalCost += prod4?.costProduct! * prodLN.coverageMonths

          firstPayment.idProductTransaction = prodLN.idProductTransaction
          firstPayment.paymentDate = formatDate(firstPaymentDate, 'yyyy-MM-dd', locale)
          firstPayment.amount = prod4?.priceList! * financeChargeRate
          firstPayment.paymentOrder = 1
          firstPayment.paymentStatus = this.PRODUCT_SOLICITANT
          prodLNPLan.push(firstPayment)
          for(let i = 1; i < prodLN.coverageMonths; i++){
            let newPayment: PaymentSubscribe = {} as PaymentSubscribe
            newPayment.idProductTransaction = prodLN.idProductTransaction
            newPayment.paymentDate = formatDate( new Date(firstPaymentDate.getFullYear(), firstPaymentDate.getMonth() + i, this.PAYMENT_INVOICE_DAY), 'yyyy-MM-dd', locale)
            newPayment.amount = prod4?.priceList! * financeChargeRate
            newPayment.paymentOrder = i + 1
            newPayment.paymentStatus = this.PRODUCT_SOLICITANT
            prodLNPLan.push(newPayment)
          }
        }

        if([this.MEMBER_FULLPAYMENT, this.EMPLOYER_FULLPAYMENT].includes(transaction.paymentMethod)){
          prodLN.coverageCost = prod4?.priceList! * prodLN.coverageMonths
          prodLN.daysToEOM = 0

          // add product price to transaction priceList
          totalPrice += prodLN.coverageCost
          totalCost += prod4?.costProduct! * prodLN.coverageMonths
          firstPayment.idProductTransaction = prodLN.idProductTransaction
          firstPayment.paymentDate = formatDate(firstPaymentDate, 'yyyy-MM-dd', locale)
          firstPayment.amount = prodLN.coverageCost
          firstPayment.paymentOrder = 1
          firstPayment.paymentStatus = this.PRODUCT_SOLICITANT
          prodLNPLan.push(firstPayment)
        }

        totalFirstPayment += firstPayment.amount
      }
    }

    transaction.totalPrice = totalPrice
    transaction.totalCost = totalCost
    transaction.financeChargeRate = 1 - financeChargeRate
    transaction.monthlyPayment = totalFirstPayment

    return {
      transaction:transaction,
      prodImgIso: prodImgIso,
      prodSSM: prodSSM,
      prodTM: prodTM,
      prodLN: prodLN,
      prodImgIsoPlan: prodImgIsoPlan,
      prodSSMPlan: prodSSMPlan,
      prodTMPlan: prodTMPlan,
      prodLNPLan: prodLNPLan,
      errorMsg: errorMsg
    } as ProductTransactionCalculate
  }

}
